import React, { useEffect, useMemo, useState } from "react";
import { formatNumber } from "../../../utils/format";
import {
  Box,
  Grid,
  Button,
  Tooltip,
  Typography,
  Breadcrumbs,
} from "@mui/material";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { FileDownload } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import Layout from "../../../layout/Layout";
import { apiData } from "../../../services/api_service";
import { productWiseInStockAPI } from "../../../config";
import ReservedStockDetailsModal from "../../../components/ReservedStockDetailsModal";
import { buildExportFileName } from "../../../utils/exportFileName";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

// Per-store breakdown for a single product (the drill-down from the
// product-wise inventory view). Mirrors the Store Inventory Report columns.
const ProductStoreReport = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const location = useLocation();

  const [tblList, setTblList] = useState([]);
  const [summary, setSummary] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [productName, setProductName] = useState(
    location.state?.product_name || ""
  );
  const [sku, setSku] = useState(location.state?.sku || "");
  const [reservedDetail, setReservedDetail] = useState(null); // { store_id, store_name }

  const buildPayload = (pageSize) => ({
    product_id: productId,
    pagination: JSON.stringify({
      pageIndex: pageSize ? 0 : pagination.pageIndex,
      pageSize: pageSize || pagination.pageSize,
    }),
    sorting: sorting.length ? JSON.stringify(sorting) : null,
    search: columnFilters.length ? JSON.stringify(columnFilters) : null,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiData("get", productWiseInStockAPI, buildPayload());
      if (response.success && response.data?.dataRows) {
        setTblList(response.data.dataRows);
        setRowCount(response.data.totalCount || 0);
        setSummary(response.data.summary || {});
        if (response.data.summary?.product_name) {
          setProductName(response.data.summary.product_name);
          setSku(response.data.summary.sku || "");
        }
      } else {
        setTblList([]);
        setRowCount(0);
        setSummary({});
      }
    } catch (e) {
      console.error("Failed to fetch product store report", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination, productId]);

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await apiData(
        "get",
        productWiseInStockAPI,
        buildPayload(10000000)
      );
      const rows = response?.data?.dataRows || [];
      if (!rows.length) {
        alert("No data to export!");
        return;
      }
      const exportData = rows.map((item, i) => ({
        "#": i + 1,
        "Store Name": item.store_name || "",
        "In Stock": Number(item.instock || 0),
        Reserved: Number(item.reserved || 0),
        Damaged: Number(item.damaged || 0),
        Missing: Number(item.missing || 0),
        Available: Number(item.available || 0),
      }));
      // TOTAL row — sum of every exported (filtered) row. The export fetches
      // the entire filtered dataset (pageSize 10000000), so this is the all-data
      // total, never a single page. (The on-screen footer is made to agree via
      // the backend all-data summary fix.)
      const sumCol = (k) => exportData.reduce((s, r) => s + (Number(r[k]) || 0), 0);
      exportData.push({
        "#": "",
        "Store Name": "TOTAL",
        "In Stock": sumCol("In Stock"),
        Reserved: sumCol("Reserved"),
        Damaged: sumCol("Damaged"),
        Missing: sumCol("Missing"),
        Available: sumCol("Available"),
      });
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Product_Store_Report");
      writeXlsxFrozen(XLSX, 
        wb,
        buildExportFileName("Product_Store_Report", [
          { label: "Product", value: productName || productId },
          sku ? { label: "SKU", value: sku } : null,
        ])
      );
    } catch (e) {
      console.error("Export failed:", e);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "store_name",
        header: "Store Name",
        size: 10,
        Footer: () => <Typography fontWeight={600}>TOTAL</Typography>,
      },
      {
        accessorKey: "instock",
        header: "In Stock",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => formatNumber(row.original.instock),
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_quantity ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "reserved",
        header: "Reserved",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const r = Number(row.original.reserved || 0);
          if (r <= 0) {
            return (
              <Typography fontSize="inherit" sx={{ fontWeight: 400 }}>
                {formatNumber(r)}
              </Typography>
            );
          }
          return (
            <Tooltip title="Click to see which CDN(s)/salesman are holding this" arrow>
              <Typography
                fontSize="inherit"
                onClick={() =>
                  setReservedDetail({
                    store_id: row.original.store_id,
                    store_name: row.original.store_name,
                  })
                }
                sx={{
                  fontWeight: 600,
                  color: "#c62828",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {formatNumber(r)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_reserved ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "damaged",
        header: "Damaged",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const d = Number(row.original.damaged || 0);
          return (
            <Typography
              fontSize="inherit"
              sx={{ fontWeight: d > 0 ? 600 : 400, color: d > 0 ? "#c62828" : "inherit" }}
            >
              {formatNumber(d)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_damaged ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "missing",
        header: "Missing",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const m = Number(row.original.missing || 0);
          return (
            <Typography
              fontSize="inherit"
              sx={{ fontWeight: m > 0 ? 600 : 400, color: m > 0 ? "#e65100" : "inherit" }}
            >
              {formatNumber(m)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_missing ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "available",
        header: "Available",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => formatNumber(row.original.available),
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_available ?? 0)}</Typography>
        ),
      },
    ],
    [summary]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid
          container
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} md={6}>
            <Typography className="page_title" textAlign="left">
              Product Stock (Store-wise)
            </Typography>
            {productName && (
              <Typography textAlign="left" variant="subtitle1">
                Product : {productName}
                {sku ? ` (${sku})` : ""}
              </Typography>
            )}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" href="/reports/stock-report">
                Stock Inventory Report
              </Link>
              <Typography color="text.primary">Product Stock</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <Grid mt={2}>
          <MaterialReactTable
            enableGlobalFilter={false}
            enableGlobalFiltering={false}
            columns={columns}
            data={tblList}
            enableTableFooter
            getRowId={(row) => `${row.store_id}`}
            initialState={{
              showColumnFilters: false,
              columnPinning: { right: ["available"] },
            }}
            enableColumnPinning
            enableRowNumbers
            rowNumberMode="original"
            enableFullScreenToggle={false}
            enableStickyHeader
            manualFiltering
            manualPagination
            manualSorting
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={rowCount}
            state={{ columnFilters, isLoading: loading, pagination, sorting }}
            muiTableFooterProps={{
              sx: {
                position: "sticky",
                bottom: 0,
                zIndex: 100,
                backgroundColor: "#fff",
                boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
            muiTableBodyCellProps={{
              sx: { padding: "3px 8px", border: "1px solid #ccc" },
            }}
            muiTableHeadCellProps={{
              sx: {
                padding: "8px",
                border: "1px solid #ccc",
                color: "#fff !important",
                fontWeight: "bold",
                backgroundColor: "#1976d2",
                "& svg": { color: "#fff !important" },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": { color: "#fff !important" },
                  },
                },
                "& .MuiTableSortLabel-icon": { color: "#fff !important" },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                borderBottom: "1px solid #ccc",
                backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
              },
            })}
            renderTopToolbarCustomActions={() => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "0.5rem" }}>
                <Tooltip title="Export data to excel sheet" arrow>
                  <Button
                    onClick={handleExport}
                    style={{ background: "#1a73e829" }}
                    startIcon={<FileDownload />}
                  >
                    Export To Excel
                  </Button>
                </Tooltip>
              </Box>
            )}
          />
        </Grid>
        <ReservedStockDetailsModal
          open={!!reservedDetail}
          onClose={() => setReservedDetail(null)}
          productId={productId}
          storeId={reservedDetail?.store_id}
          productName={productName}
          storeName={reservedDetail?.store_name}
        />
      </Grid>
    </Layout>
  );
};

export default ProductStoreReport;
